import { Injectable } from '@angular/core';
import { Headers, Http } from '@angular/http';
import { AppConfig } from 'app/app.config';
import { AuthService } from 'app/services/auth.service';
import { PresentationRepository } from 'app/services/database/presentation.repository';
import { StorageCategories, StorageService } from 'app/services/database/storage.service';
import { FileService } from 'app/services/files.service';
import { PresentationHandler } from 'app/services/presentation/presentationHandler';
import { Logger } from 'app/shared/logger';
import { LoggerFactory } from 'app/shared/loggerFactory';
import { FileModel, FileModelBuilder } from 'app/views/presentations/models/file.model';
import { PresentationModel, Section } from 'app/views/presentations/models/presentation.model';
import { SlideshowModel } from 'app/views/presentations/models/slideshow.model';
import { SlideshowWidget } from 'app/views/presentations/widgets/slideshow/slideshow.widget';

@Injectable()
export class FileHandler extends PresentationHandler {

    private logger: Logger;

    constructor(
        private auth: AuthService,
        private presentationRepository: PresentationRepository,
        private fileService: FileService,
        loggerFactory: LoggerFactory
    ) {
        super();
        this.logger = loggerFactory.create('ThumbnailHandler');

        // Set the file handler to a higher priority
        this.priority = -1000;
    }

    public async onSave(presentation: PresentationModel) {
        await this.purgeOldFiles(presentation);

        for (let i = 0; i < presentation.sections.length; i++) {
            const section = presentation.sections[i];
            if (!section.files) continue;
            for (let file of section.files.filter(x => x instanceof FileModelBuilder)) {
                const builder = file as FileModelBuilder;
                const uploadedFile = await this.fileService.uploadFile(presentation.id, i.toString(), builder);

                builder.url = uploadedFile.url;
                builder.id = uploadedFile.id;
                delete builder.file;
            }
        }
    }

    public async onDelete(presentation: PresentationModel) {
        // Delete the presentation folder
        await this.fileService.deletePresentationFiles(presentation.id);
    }

    private async purgeOldFiles(presentation: PresentationModel) {
        if (!await this.presentationRepository.exists(presentation.id)) { return; }

        const originalPresentation = await this.presentationRepository.get(presentation.id);
        const numberOfSections = Math.max(originalPresentation.sections.length, presentation.sections.length);

        for (let i = 0; i < numberOfSections; ++i) {
            let originalSection = numberOfSections > originalPresentation.sections.length ? undefined : originalPresentation.sections[i];
            let currentSection = numberOfSections > presentation.sections.length ? undefined : presentation.sections[i];
            await this.purgeOldFilesInSection(presentation.id, i.toString(), originalSection, currentSection);
        }
    }

    private async purgeOldFilesInSection(presentationId: string, sectionId: string, oldSection: Section, newSection: Section) {
        if ((!newSection || !newSection.files || !newSection.files.length) && oldSection && oldSection.files && oldSection.files.length) {
            this.logger.debug(`Deleting section ${sectionId} from storage`);
            this.fileService.deleteSectionFiles(presentationId, sectionId);
        } else {
            if (!oldSection || oldSection.files === undefined) { return; }

            let newFilesById = new Map<string, FileModel>(newSection.files ? newSection.files.map(x => [x.id, x] as [string, FileModel]) : []);
            let filesToDelete = [];
            for (const file of oldSection.files) {
                if (!newFilesById.has(file.id)) {
                    filesToDelete.push(file);
                }
            }

            for (const file of filesToDelete) {
                try {
                    this.logger.debug('Deleting file ' + file.id);
                    await this.fileService.deleteFile(presentationId, sectionId, file.id);
                } catch (err) {
                    this.logger.error(`An error occured while deleting the following file: ${file.id}`, err);
                }
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/services/presentation/fileHandler.ts